import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Button,
  TableHead,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

const API_URL = API_ENDPOINTS.USER_TYPES;

const UserTypesListContent = () => {
  const [userTypes, setUserTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user } = useAuth();
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
    name: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchUserTypes();
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchUserTypes = async () => {
    try {
      const response = await axios.get(API_URL.BASE);
      const data = response.data;
      let userTypesArray = [];
      if (Array.isArray(data)) {
        userTypesArray = data;
      } else if (data && Array.isArray(data.userTypes)) {
        userTypesArray = data.userTypes;
      }
      setUserTypes(userTypesArray);
    } catch (error) {
      console.error("Error fetching user types:", error);
      setSnackbar({
        open: true,
        message: "Failed to load user types",
        severity: "error",
      });
      setUserTypes([]);
    }
  };

  const handleDeleteClick = (id, name) => {
    setDeleteDialog({
      open: true,
      id,
      name,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(API_URL.DELETE(deleteDialog.id));
      setUserTypes((prevUserTypes) => 
        prevUserTypes.filter((userType) => userType.id !== deleteDialog.id)
      );
      setSnackbar({
        open: true,
        message: "User type deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting user type:", error);
      let userMessage = "Failed to delete user type.";
      if (
        error.response?.data?.message &&
        error.response.data.message.includes("foreign key constraint fails")
      ) {
        userMessage =
          "Cannot delete this user type because it is assigned to users. Please reassign or remove those users first.";
      }
      setSnackbar({
        open: true,
        message: userMessage,
        severity: "error",
      });
    }
    setDeleteDialog({ open: false, id: null, name: "" });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null, name: "" });
  };

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await axios.put(API_URL.EDIT(id), { publish: !currentStatus });
      setUserTypes((prevUserTypes) =>
        prevUserTypes.map((userType) =>
          userType.id === id ? { ...userType, publish: !currentStatus } : userType
        )
      );
      setSnackbar({
        open: true,
        message: `User type ${!currentStatus ? "published" : "unpublished"} successfully`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating publish status:", error);
      setSnackbar({
        open: true,
        message: "Failed to update publish status",
        severity: "error",
      });
    }
  };

  const filteredUserTypes = Array.isArray(userTypes)
    ? userTypes.filter((userType) =>
        userType.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", padding: "25px" }}>
      {/* <NavList /> */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: "25px" }}>
          <input
            type="text"
            placeholder="Search user type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "8px", width: "200px", borderRadius: "5px", border: "1px solid #ccc" }}
          />
          {user.permissions.includes("add-userTypes") && (
            <Link to="/ecommerce/create-userTypes">
              <Button variant="outlined" color="primary">
                <AddIcon /> Add New User Type
              </Button>
            </Link>
          )}
        </Box>

        <TableContainer className="custom-table" component={Paper}>
          <Table sx={{ minWidth: 1000 }} aria-label="User Types Table">
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Publish</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUserTypes
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((userType, index) => (
                  <TableRow key={userType.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{userType.name}</TableCell>
                    <TableCell>
                      <Switch
                        checked={userType.publish}
                        onChange={() => handleTogglePublish(userType.id, userType.publish)}
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      {/* {user.permissions.includes("view-userTypes") && (
                        <Link to={`/ecommerce/userTypes-list/details/${userType.id}`}>
                          <IconButton color="info">
                            <VisibilityIcon />
                          </IconButton>
                        </Link>
                      )} */}
                      {user.permissions.includes("edit-userTypes") && (
                        <Link to={`/ecommerce/edit-userTypes/${userType.id}`}>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                        </Link>
                      )}
                      {user.permissions.includes("delete-userTypes") && (
                        <IconButton 
                          color="error" 
                          onClick={() => handleDeleteClick(userType.id, userType.name)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  count={filteredUserTypes.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the user type "{deleteDialog.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default UserTypesListContent;